import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, FileText, FileImage, Download } from "lucide-react";
import { fmtDate } from "@/lib/format";

const CATS = ["script","music","design","costume","technical","reference","other"];

function formatBytes(b) {
  if (!b) return "";
  const k = 1024;
  const sizes = ["B","KB","MB","GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function Documents() {
  const { user } = useAuth();
  // Backend allows super_admin, org_admin, and production_staff to upload/
  // delete. Everyone else — cast, producer, support_staff — is read-only.
  const canEdit = user.role === "org_admin" || user.role === "production_staff" || user.role === "super_admin";
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("script");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      const pid = localStorage.getItem("sc_pid");
      if (pid) fd.append("production_id", pid);
      await api.post("/documents", fd);
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const downloadUrl = (d) => {
    if (d.download_url) return `${process.env.REACT_APP_BACKEND_URL}${d.download_url}`;
    return `${API_BASE}/documents/${d.id}/download`; // relies on same-origin cookie
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="documents-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Library</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Documents</h1>
          <p className="mt-1 text-zinc-400 text-sm">Scripts, scores, design decks and technical PDFs.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-[#121212] border-white/10 w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={fileRef} type="file" data-testid="doc-file-input" className="hidden" onChange={upload} />
            <Button
              data-testid="upload-doc-btn"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="bg-gold text-black hover:bg-gold-hover"
            >
              <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        )}
      </header>

      <div className="rounded-lg border border-white/10 bg-[#121212] divide-y divide-white/5">
        {docs.length === 0 && <div className="p-8 text-sm text-zinc-500 text-center">No documents yet. Upload scripts, scores, or design references.</div>}
        {docs.map((d) => {
          const isImg = (d.content_type || "").startsWith("image/");
          return (
            <div key={d.id} className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-md grid place-items-center bg-white/5 text-gold">
                {isImg ? <FileImage className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{d.name}</div>
                <div className="text-xs text-zinc-500">
                  {d.category} · {formatBytes(d.size)} · {d.uploaded_by_name} · {fmtDate(d.created_at)}
                </div>
              </div>
              <a
                data-testid={`download-${d.id}`}
                href={downloadUrl(d)}
                target="_blank"
                rel="noreferrer"
                className="p-2 border border-white/10 rounded hover:bg-white/5 text-zinc-300"
              >
                <Download className="w-4 h-4" />
              </a>
              {canEdit && (
                <button data-testid={`del-doc-${d.id}`} onClick={() => del(d.id)} className="p-2 border border-white/10 rounded hover:bg-red-500/10 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
